import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { User } from '../services/api';
import { API_BASE_URL } from '../config/env';

export const clearStoredAuth = () => {
  localStorage.removeItem('zitadel_access_token');
  localStorage.removeItem('zitadel_id_token');
  localStorage.removeItem('zitadel_refresh_token');
  localStorage.removeItem('user');
};

interface CustomAuthContextType {
  user: User | null;
  authToken: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  logout: () => void;
  updateUser: (updatedUser: User) => void;
  refreshUser: () => Promise<void>;
}

const CustomAuthContext = createContext<CustomAuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(CustomAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a CustomAuthProvider');
  }
  return context;
};

interface CustomAuthProviderProps {
  children: ReactNode;
}

interface SessionResponse {
  session: {
    id: string;
    factors: {
      user?: {
        id: string;
        loginName: string;
        displayName: string;
      };
    };
  };
  user?: User;
}

export const CustomAuthProvider: React.FC<CustomAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAuth();
  }, []);

  const initAuth = async () => {
    try {
      // Check if we have a real Zitadel access token in localStorage
      const storedAccessToken = localStorage.getItem('zitadel_access_token');
      const storedUser = localStorage.getItem('user');

      if (storedAccessToken && storedUser) {
        setAuthToken(storedAccessToken);
        setUser(JSON.parse(storedUser));
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      // Clear invalid auth data
      clearStoredAuth();
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!authToken) return;

    try {
      const idToken = localStorage.getItem('zitadel_id_token');
      const response = await axios.get<{ user: User }>(
        `${API_BASE_URL}/auth/profile`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            ...(idToken ? { 'x-id-token': idToken } : {}),
          },
        }
      );

      if (response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const logout = async () => {
    try {
      // Optional: Call logout endpoint if you want to invalidate tokens server-side
      // await axios.post(`${API_BASE_URL}/auth/logout`);
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      clearStoredAuth();
      setAuthToken(null);
      setUser(null);

      // Redirect to login
      window.location.href = '/login';
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const value: CustomAuthContextType = {
    user,
    authToken,
    loading,
    isAuthenticated: !!user && !!authToken,
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <CustomAuthContext.Provider value={value}>
      {children}
    </CustomAuthContext.Provider>
  );
};
